import Users from '@/assets/icons/Users';
import AffiliateLinksDialog from '@/components/shared/AffiliateLinksDialog/AffiliateLinksDialog';
import CTATable from '@/components/shared/TableListCustom/CTATable';
import { EmptyItem } from '@/components/shared/EmptyItem/EmptyItem';
import { PaginationRange } from '@/components/shared/PaginationRange/PaginationRange';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { mapReportLinksToPromotionLinks } from '@/utils/affiliatePromotionLinkHelpers';
import type { AffiliateReportFilterOption } from '@/@types/AffiliateReport';
import {
  affiliateToFilterOption,
  buildAnalyticsPath,
  buildUtmPanelPath,
  productsToFilterOptions,
} from '@/utils/reportNavigation';
import {
  BarChart3,
  Edit,
  ExternalLink,
  HelpCircle,
  Link as LinkIcon,
} from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AffiliateReportRow,
  AffiliateReportTableData,
} from '@/@types/AffiliateReport';

function HeaderCell({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span>{title}</span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="w-3.5 h-3.5 text-zinc-500 cursor-help" />
          </TooltipTrigger>
          <TooltipContent className="max-w-[280px]">
            <p className="text-xs">{description}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function getCommissionDisplay(
  affiliate: AffiliateReportRow,
  variableLabel: string,
): { label: string; isVariable: boolean } {
  const { products, commissionFormatted } = affiliate;

  if (products.length <= 1) {
    return { label: commissionFormatted, isVariable: false };
  }

  const uniqueValues = new Set(products.map((p) => p.commissionFormatted));

  if (uniqueValues.size <= 1) {
    return { label: products[0].commissionFormatted, isVariable: false };
  }

  return { label: variableLabel, isVariable: true };
}

function TableSkeleton() {
  return (
    <div className="px-4 sm:px-6">
      {Array.from({ length: 10 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center h-16 border-b border-zinc-800 gap-3 sm:gap-5"
        >
          <div className="flex-1 min-w-0 space-y-1.5">
            <Skeleton className="h-3.5 w-28 sm:w-36 bg-zinc-800" />
            <Skeleton className="h-3 w-20 sm:w-24 bg-zinc-800" />
          </div>
          <Skeleton className="h-4 w-10 bg-zinc-800 hidden sm:block shrink-0" />
          <Skeleton className="h-4 w-16 bg-zinc-800 shrink-0" />
          <Skeleton className="h-4 w-16 bg-zinc-800 hidden md:block shrink-0" />
          <Skeleton className="h-4 w-14 bg-zinc-800 hidden md:block shrink-0" />
          <Skeleton className="h-7 w-12 bg-zinc-800 rounded hidden sm:block shrink-0" />
          <Skeleton className="h-7 w-7 bg-zinc-800 rounded shrink-0" />
        </div>
      ))}
    </div>
  );
}

interface Props {
  tableData: AffiliateReportTableData;
  isLoading: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  dateRange: DateRange | undefined;
  products: AffiliateReportFilterOption[];
  offers: AffiliateReportFilterOption[];
  checkouts: AffiliateReportFilterOption[];
  onOpenDetails: (affiliate: AffiliateReportRow) => void;
  onOpenProductPicker: (affiliate: AffiliateReportRow) => void;
}

export default function AffiliateReportTable({
  tableData,
  isLoading,
  currentPage,
  setCurrentPage,
  dateRange,
  products,
  offers,
  checkouts,
  onOpenDetails,
  onOpenProductPicker,
}: Props) {
  const { t } = useTranslation(['reports', 'affiliates', 'common']);
  const navigate = useNavigate();
  const [linksModalAffiliate, setLinksModalAffiliate] =
    useState<AffiliateReportRow | null>(null);

  const linksModalPromotionLinks = useMemo(
    () => mapReportLinksToPromotionLinks(linksModalAffiliate?.links),
    [linksModalAffiliate?.links],
  );

  const isEmpty = !isLoading && tableData.data.length === 0;
  const hasData = !isLoading && tableData.data.length > 0;

  const navigateToAnalytics = (affiliate: AffiliateReportRow) => {
    navigate(
      buildAnalyticsPath({
        dateRange,
        role: 'producer',
        affiliates: [affiliateToFilterOption(affiliate)],
        products,
        offers,
        checkouts,
      }),
    );
  };

  const navigateToUtm = (affiliate: AffiliateReportRow) => {
    const resolvedProducts =
      products.length > 0 ? products : productsToFilterOptions(affiliate.products);

    navigate(
      buildUtmPanelPath({
        dateRange,
        affiliates: [affiliateToFilterOption(affiliate)],
        products: resolvedProducts,
        offers,
        checkouts,
      }),
    );
  };

  return (
    <>
      <Card className="overflow-hidden min-w-0">
        <div className="p-4 border-b border-zinc-800">
          <h2 className="text-base font-semibold">
            {t('reports:affiliateReport.table.title')}
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            {t('reports:affiliateReport.table.subtitle')}
          </p>
        </div>

        {isLoading && <TableSkeleton />}

        {isEmpty && (
          <EmptyItem
            title={t('reports:affiliateReport.table.empty_title')}
            description={t('reports:affiliateReport.table.empty_description')}
            icon={<Users />}
          />
        )}

        {hasData && (
          <div className="overflow-x-auto w-full">
            <Table data-cy="affiliate-report-table">
              <TableHeader className="bg-[#18181b] text-xs font-semibold text-muted uppercase h-10">
                <TableRow className="h-10 hover:bg-transparent">
                  <TableCell>{t('affiliates:user')}</TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <HeaderCell
                      title={t('affiliates:commission')}
                      description={t(
                        'reports:affiliateReport.table.commission_rule_description',
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <HeaderCell
                      title={t('affiliates:gross_revenue')}
                      description={t('affiliates:gross_revenue_description')}
                    />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <HeaderCell
                      title={t('affiliates:total_commission')}
                      description={t('affiliates:total_commission_description')}
                    />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <HeaderCell
                      title={t('affiliates:producer_profit')}
                      description={t('affiliates:producer_profit_description')}
                    />
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    {t('affiliates:links')}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHeader>

              <TableBody>
                {tableData.data.map((affiliate) => (
                  <TableRow
                    key={affiliate.id}
                    className="h-16 border-b border-b-colorBorder hover:bg-transparent"
                  >
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium">{affiliate.name}</span>
                        <span className="text-xs text-zinc-400 truncate max-w-[180px] sm:max-w-none">
                          {affiliate.email}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell className="hidden sm:table-cell">
                      {(() => {
                        const commission = getCommissionDisplay(
                          affiliate,
                          t(
                            'reports:affiliateReport.table.commission_rule_variable',
                          ),
                        );

                        if (!commission.isVariable) {
                          return commission.label;
                        }

                        return (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="underline decoration-dotted decoration-zinc-500 cursor-help">
                                  {commission.label}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[260px]">
                                <p className="text-xs font-semibold mb-1.5">
                                  {t(
                                    'reports:affiliateReport.table.commission_rule_breakdown_title',
                                  )}
                                </p>
                                <ul className="text-xs space-y-1">
                                  {affiliate.products.map((product) => (
                                    <li
                                      key={product.productId}
                                      className="flex justify-between gap-3"
                                    >
                                      <span className="text-zinc-400 truncate">
                                        {product.productName}
                                      </span>
                                      <span className="shrink-0">
                                        {product.commissionFormatted}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })()}
                    </TableCell>

                    <TableCell className="tabular-nums">
                      {affiliate.metrics.revenue}
                    </TableCell>

                    <TableCell className="hidden md:table-cell tabular-nums">
                      {affiliate.metrics.commissionPaid}
                    </TableCell>

                    <TableCell className="hidden md:table-cell tabular-nums">
                      {affiliate.metrics.profit}
                    </TableCell>

                    <TableCell className="hidden sm:table-cell">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1 h-8 px-2"
                        onClick={() => setLinksModalAffiliate(affiliate)}
                      >
                        <LinkIcon className="w-4 h-4" />
                        {affiliate.links.length}
                      </Button>
                    </TableCell>

                    <TableCell className="w-[50px]">
                      <CTATable
                        items={[
                          {
                            id: 1,
                            label: 'details',
                            icon: <ExternalLink className="w-4 h-4" />,
                            action: () => onOpenDetails(affiliate),
                          },
                          {
                            id: 2,
                            label: 'report_analytics',
                            icon: <BarChart3 className="w-4 h-4" />,
                            action: () => navigateToAnalytics(affiliate),
                          },
                          {
                            id: 3,
                            label: 'report_utm',
                            icon: <ExternalLink className="w-4 h-4" />,
                            action: () => navigateToUtm(affiliate),
                          },
                          {
                            id: 4,
                            label: 'edit_affiliate',
                            icon: <Edit className="w-4 h-4" />,
                            action: () => onOpenProductPicker(affiliate),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {hasData && tableData.meta.last_page > 1 && (
          <div className="p-4 border-t border-t-colorBorder">
            <PaginationRange
              currentPage={currentPage}
              totalPages={tableData.meta.last_page}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </Card>

      <AffiliateLinksDialog
        open={!!linksModalAffiliate}
        onOpenChange={(open) => !open && setLinksModalAffiliate(null)}
        links={linksModalPromotionLinks}
      />
    </>
  );
}
